import { useEffect, useState, type ReactNode } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthStore } from "@/stores/authStore";

/**
 * Modo mantenimiento global.
 *
 * Lee el documento `appConfig/maintenance`:
 *   {
 *     enabled: boolean,
 *     message?: string,        // mensaje opcional para usuarios
 *     startedAt?: Timestamp,
 *     startedBy?: string,
 *   }
 *
 * Cuando `enabled === true`:
 *  - Bloquea la UI a TODOS los usuarios excepto admin_union y admin_association
 *    (que pueden seguir trabajando para ejecutar la migración).
 *  - Para los admins muestra una banda persistente recordando que está activo.
 *
 * Para activar/desactivar usar `scripts/maintenance.mjs <on|off|status>`.
 *
 * Notas de seguridad:
 *  - Esto es una gate de UI; las reglas de Firestore son la fuente de verdad
 *    real. Un atacante con consola puede saltarse este componente, pero las
 *    reglas seguirán bloqueando escrituras peligrosas durante la migración.
 *  - Si el documento no existe o no se puede leer, asumimos `enabled=false`
 *    (failsafe: nunca bloqueamos por error de red).
 */

interface MaintenanceConfig {
  enabled: boolean;
  message?: string;
}

export default function MaintenanceGate({ children }: { children: ReactNode }) {
  const [cfg, setCfg] = useState<MaintenanceConfig>({ enabled: false });
  const profile = useAuthStore((s) => s.profile);

  useEffect(() => {
    const ref = doc(db, "appConfig", "maintenance");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setCfg({ enabled: false });
          return;
        }
        const data = snap.data() as Partial<MaintenanceConfig>;
        setCfg({
          enabled: !!data.enabled,
          message: data.message,
        });
      },
      (err) => {
        // Failsafe: si fallan las reglas, no bloqueamos
        console.warn("[MaintenanceGate] no se pudo suscribir:", err.message);
        setCfg({ enabled: false });
      },
    );
    return () => unsub();
  }, []);

  const isAdmin =
    profile?.primaryRole === "admin_union" ||
    profile?.primaryRole === "admin_association";

  if (cfg.enabled && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-amber-50 px-4">
        <div className="max-w-lg w-full bg-white rounded-lg shadow-lg p-8 text-center border-2 border-amber-300">
          <div className="text-5xl mb-4">🛠️</div>
          <h1 className="text-2xl font-bold text-amber-700 mb-3">
            Sistema en mantenimiento
          </h1>
          <p className="text-gray-700 mb-4">
            {cfg.message ??
              "Estamos realizando una actualización importante. Vuelve a intentar en unos minutos."}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="bg-amber-600 hover:bg-amber-700 text-white font-semibold px-4 py-2 rounded"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {cfg.enabled && isAdmin && (
        <div
          role="alert"
          className="fixed top-0 inset-x-0 z-[9998] bg-amber-400 text-amber-900 text-sm font-medium text-center py-1 shadow"
        >
          ⚠️ Modo mantenimiento ACTIVO — solo administradores pueden operar.
        </div>
      )}
      {children}
    </>
  );
}
